import { lazy, Suspense, type ReactElement } from 'react';
import { Navigate, type RouteObject } from 'react-router';

import { AppLayout, AuthLayout, PublicLayout } from '@app/layouts';

import { Spinner } from '@shared/components';

import { RequireAuth } from './RequireAuth';

const LoginPage = lazy(async () =>
  import('@features/auth').then((m) => ({ default: m.LoginPage })),
);

const ForgotPasswordPage = lazy(async () =>
  import('@features/auth').then((m) => ({ default: m.ForgotPasswordPage })),
);

const ResetPasswordPage = lazy(async () =>
  import('@features/auth').then((m) => ({ default: m.ResetPasswordPage })),
);

const AcceptInvitePage = lazy(async () =>
  import('@features/auth').then((m) => ({ default: m.AcceptInvitePage })),
);

const DashboardPage = lazy(async () =>
  import('@features/dashboard').then((m) => ({ default: m.DashboardPage })),
);

const UsersPage = lazy(async () =>
  import('@features/users').then((m) => ({ default: m.UsersPage })),
);

const CreateUserPage = lazy(async () =>
  import('@features/users').then((m) => ({ default: m.CreateUserPage })),
);

const EditUserPage = lazy(async () =>
  import('@features/users').then((m) => ({ default: m.EditUserPage })),
);

const VehiclesPage = lazy(async () =>
  import('@features/vehicles').then((m) => ({ default: m.VehiclesPage })),
);

const CreateVehiclePage = lazy(async () =>
  import('@features/vehicles').then((m) => ({ default: m.CreateVehiclePage })),
);

const EditVehiclePage = lazy(async () =>
  import('@features/vehicles').then((m) => ({ default: m.EditVehiclePage })),
);

const DriversPage = lazy(async () =>
  import('@features/drivers').then((m) => ({ default: m.DriversPage })),
);

const CreateDriverPage = lazy(async () =>
  import('@features/drivers').then((m) => ({ default: m.CreateDriverPage })),
);

const EditDriverPage = lazy(async () =>
  import('@features/drivers').then((m) => ({ default: m.EditDriverPage })),
);

const FuelStationsPage = lazy(async () =>
  import('@features/fuel-stations').then((m) => ({ default: m.FuelStationsPage })),
);

const RefuelingsPage = lazy(async () =>
  import('@features/refuelings').then((m) => ({ default: m.RefuelingsPage })),
);

const EditRefuelingPage = lazy(async () =>
  import('@features/refuelings').then((m) => ({ default: m.EditRefuelingPage })),
);

const IndicatorsReportPage = lazy(async () =>
  import('@features/refuelings').then((m) => ({ default: m.IndicatorsReportPage })),
);

const DeviationPage = lazy(async () =>
  import('@features/reports').then((m) => ({ default: m.DeviationPage })),
);

const CreateFuelStationPage = lazy(async () =>
  import('@features/fuel-stations').then((m) => ({ default: m.CreateFuelStationPage })),
);

const EditFuelStationPage = lazy(async () =>
  import('@features/fuel-stations').then((m) => ({ default: m.EditFuelStationPage })),
);

function PageLoading(): ReactElement {
  return (
    <output className="flex min-h-[40vh] items-center justify-center">
      <Spinner size="lg" label="Carregando…" />
    </output>
  );
}

function NotFound(): ReactElement {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
      <h1 className="text-3xl font-semibold text-surface-fg">404</h1>
      <p className="text-sm text-surface-fg/70">A página que você procura não existe.</p>
    </div>
  );
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    element: <AuthLayout />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<PageLoading />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <Suspense fallback={<PageLoading />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: '/reset-password',
        element: (
          <Suspense fallback={<PageLoading />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: '/accept-invite',
        element: (
          <Suspense fallback={<PageLoading />}>
            <AcceptInvitePage />
          </Suspense>
        ),
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: '/home',
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: '/dashboard/*',
            element: (
              <Suspense fallback={<PageLoading />}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: '/users',
            element: (
              <Suspense fallback={<PageLoading />}>
                <UsersPage />
              </Suspense>
            ),
          },
          {
            path: '/users/new',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CreateUserPage />
              </Suspense>
            ),
          },
          {
            path: '/users/:id/edit',
            element: (
              <Suspense fallback={<PageLoading />}>
                <EditUserPage />
              </Suspense>
            ),
          },
          {
            path: '/vehicles',
            element: (
              <Suspense fallback={<PageLoading />}>
                <VehiclesPage />
              </Suspense>
            ),
          },
          {
            path: '/vehicles/new',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CreateVehiclePage />
              </Suspense>
            ),
          },
          {
            path: '/vehicles/:id/edit',
            element: (
              <Suspense fallback={<PageLoading />}>
                <EditVehiclePage />
              </Suspense>
            ),
          },
          {
            path: '/drivers',
            element: (
              <Suspense fallback={<PageLoading />}>
                <DriversPage />
              </Suspense>
            ),
          },
          {
            path: '/drivers/new',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CreateDriverPage />
              </Suspense>
            ),
          },
          {
            path: '/drivers/:id/edit',
            element: (
              <Suspense fallback={<PageLoading />}>
                <EditDriverPage />
              </Suspense>
            ),
          },
          {
            path: '/fuel-stations',
            element: (
              <Suspense fallback={<PageLoading />}>
                <FuelStationsPage />
              </Suspense>
            ),
          },
          {
            path: '/fuel-stations/new',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CreateFuelStationPage />
              </Suspense>
            ),
          },
          {
            path: '/fuel-stations/:id/edit',
            element: (
              <Suspense fallback={<PageLoading />}>
                <EditFuelStationPage />
              </Suspense>
            ),
          },
          {
            path: '/refuelings',
            element: (
              <Suspense fallback={<PageLoading />}>
                <RefuelingsPage />
              </Suspense>
            ),
          },
          {
            path: '/refuelings/:id/edit',
            element: (
              <Suspense fallback={<PageLoading />}>
                <EditRefuelingPage />
              </Suspense>
            ),
          },
          {
            path: '/reports/indicators',
            element: (
              <Suspense fallback={<PageLoading />}>
                <IndicatorsReportPage />
              </Suspense>
            ),
          },
          {
            path: '/reports/deviation',
            element: (
              <Suspense fallback={<PageLoading />}>
                <DeviationPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    element: <PublicLayout />,
    children: [{ path: '*', element: <NotFound /> }],
  },
];
